import React, { useEffect, useRef } from 'react';
import styles from './RangeSlider.module.css';

export interface RangeSliderProps {
  id?: string;
  value: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void; // Or callback returning a number
  min: number;
  max: number;
  step?: number;
  disabled?: boolean;
  className?: string; // For the slider input itself
  containerClassName?: string; // For the main div container
  showValueDisplay?: boolean; // Default true
  formatValueDisplay?: (value: number) => string; // Optional, to format the displayed value
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  id,
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
  className = '',
  containerClassName = '',
  showValueDisplay = true,
  formatValueDisplay,
}) => {
  const sliderRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (sliderRef.current) {
      const val = Number(value); // Ensure value is a number
      const minVal = Number(min);
      const maxVal = Number(max);
      if (maxVal === minVal) { // Avoid division by zero
        sliderRef.current.style.setProperty('--slider-fill-percent', `0%`);
        return;
      }
      const percentage = ((val - minVal) / (maxVal - minVal)) * 100;
      sliderRef.current.style.setProperty('--slider-fill-percent', `${Math.max(0, Math.min(100, percentage))}%`);
    }
  }, [value, min, max]);

  const displayValue = formatValueDisplay ? formatValueDisplay(Number(value)) : Number(value).toString();

  return (
    <div className={`${styles.rangeSliderContainer} ${containerClassName}`}>
      {showValueDisplay && (
        <span className={styles.valueDisplay}>{displayValue}</span>
      )}
      <input
        ref={sliderRef}
        type="range"
        id={id}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
        disabled={disabled}
        className={`${styles.rangeSlider} ${className}`}
      />
    </div>
  );
};